import React, { useEffect, useRef } from 'react';
import { Box, Typography, Paper } from '@mui/material';
import gsap from 'gsap';

/**
 * @component TradingPlatformAnimation
 * @description Animated visualization of the minerals trading platform
 *
 * SEO Considerations:
 * - Provides text alternatives for animation content
 * - Uses semantic HTML structure
 * - Includes keyword-rich headings and descriptions
 * - Ensures content is accessible even when JavaScript is disabled
 */
const TradingPlatformAnimation = () => {
  // References for animation elements
  const containerRef = useRef(null);
  const minerRef = useRef(null);
  const traderRef = useRef(null);
  const manufacturerRef = useRef(null);
  const platformRef = useRef(null);
  const mineralRef = useRef(null);
  const documentRef = useRef(null);
  const paymentRef = useRef(null);

  /**
   * Initialize animation with SEO considerations
   * - Animation enhances user experience but doesn't hide essential content
   * - All key information is available in text form for search engines
   */
  useEffect(() => {
    const timeline = gsap.timeline({
      repeat: -1,
      repeatDelay: 1,
      defaults: { duration: 1, ease: "power2.inOut" }
    });

    // Reset positions
    gsap.set([mineralRef.current, documentRef.current, paymentRef.current], {
      autoAlpha: 0,
      scale: 0.8
    });

    // Animation sequence
    timeline
      // Miner uploads mineral data
      .to(mineralRef.current, {
        autoAlpha: 1,
        scale: 1,
        x: "0%",
        y: "0%"
      })
      .to(mineralRef.current, {
        x: "+=100",
        y: "-=50"
      }, "+=0.5")
      .to(mineralRef.current, {
        x: "+=100",
        y: "+=0"
      }, "+=0.3")

      // Platform verifies data
      .to(documentRef.current, {
        autoAlpha: 1,
        scale: 1
      }, "+=0.5")
      .to(documentRef.current, {
        x: "-=100",
        y: "+=50"
      }, "+=0.5")

      // Trader makes purchase
      .to(paymentRef.current, {
        autoAlpha: 1,
        scale: 1
      }, "+=0.5")
      .to(paymentRef.current, {
        x: "-=100",
        y: "-=50"
      }, "+=0.5")

      // Manufacturer receives minerals
      .to(mineralRef.current, {
        x: "-=100",
        y: "+=50"
      }, "+=0.5")

      // Reset animation
      .to([mineralRef.current, documentRef.current, paymentRef.current], {
        autoAlpha: 0,
        scale: 0.8,
        x: 0,
        y: 0
      }, "+=1");

    return () => {
      timeline.kill();
    };
  }, []);

  return (
    <Paper
      ref={containerRef}
      aria-label="Interactive visualization of the Laprecio ethical mineral trading platform"
      sx={{
        position: 'relative',
        height: 400,
        width: '100%',
        overflow: 'hidden',
        borderRadius: 3,
        border: '2px solid #f5e5a5',
        borderLeft: '6px solid #eab308',
        boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
        background: 'linear-gradient(to right, #fffef0, #fffde7)',
        p: 2,
        mb: 4
      }}
    >
      <Typography variant="h3" align="center" gutterBottom sx={{ fontWeight: 'bold', mb: 4 }}>
        Laprecio: Connecting the Ethical Minerals Supply Chain
      </Typography>
      <Typography variant="body1" align="center" paragraph sx={{ mb: 4 }}>
        Our platform connects miners, traders, and manufacturers in a secure and transparent ecosystem for ethical mineral trading
      </Typography>

      {/* Platform center */}
      <Box
        ref={platformRef}
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 140,
          height: 140,
          borderRadius: '50%',
          bgcolor: '#eab308',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
          zIndex: 1
        }}
      >
        <Typography variant="h6" align="center" sx={{ color: 'white', fontWeight: 'bold' }}>
          LAPRECIO
        </Typography>
        <Typography variant="caption" align="center" sx={{ color: 'white', mt: 0.5, fontSize: '0.7rem' }}>
          TRADING PLATFORM
        </Typography>
      </Box>

      {/* Miner node */}
      <Box
        ref={minerRef}
        sx={{
          position: 'absolute',
          top: '30%',
          left: '15%',
          width: 110,
          height: 110,
          borderRadius: '50%',
          bgcolor: 'white',
          border: '2px solid #eab308',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 6px 16px rgba(0,0,0,0.15)',
          zIndex: 2
        }}
      >
        <Box
          sx={{
            width: 60,
            height: 60,
            borderRadius: '50%',
            bgcolor: 'rgba(234, 179, 8, 0.1)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 1
          }}
        >
          <Typography variant="h2" sx={{ fontSize: '2.5rem' }}>🏗️</Typography>
        </Box>
        <Typography variant="body1" sx={{ fontWeight: 'bold', fontSize: '1rem' }}>
          Miner
        </Typography>
      </Box>

      {/* Trader node */}
      <Box
        ref={traderRef}
        sx={{
          position: 'absolute',
          top: '30%',
          right: '15%',
          width: 110,
          height: 110,
          borderRadius: '50%',
          bgcolor: 'white',
          border: '2px solid #eab308',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 6px 16px rgba(0,0,0,0.15)',
          zIndex: 2
        }}
      >
        <Box
          sx={{
            width: 60,
            height: 60,
            borderRadius: '50%',
            bgcolor: 'rgba(234, 179, 8, 0.1)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 1
          }}
        >
          <Typography variant="h2" sx={{ fontSize: '2.5rem' }}>📊</Typography>
        </Box>
        <Typography variant="body1" sx={{ fontWeight: 'bold', fontSize: '1rem' }}>
          Trader
        </Typography>
      </Box>

      {/* Manufacturer node */}
      <Box
        ref={manufacturerRef}
        sx={{
          position: 'absolute',
          bottom: '15%',
          left: '50%',
          transform: 'translateX(-50%)',
          width: 110,
          height: 110,
          borderRadius: '50%',
          bgcolor: 'white',
          border: '2px solid #eab308',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 6px 16px rgba(0,0,0,0.15)',
          zIndex: 2
        }}
      >
        <Box
          sx={{
            width: 60,
            height: 60,
            borderRadius: '50%',
            bgcolor: 'rgba(234, 179, 8, 0.1)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 1
          }}
        >
          <Typography variant="h2" sx={{ fontSize: '2.5rem' }}>🏭</Typography>
        </Box>
        <Typography variant="body1" sx={{ fontWeight: 'bold', fontSize: '1rem' }}>
          Manufacturer
        </Typography>
      </Box>

      {/* Connection lines */}
      <svg
        width="100%"
        height="100%"
        style={{ position: 'absolute', top: 0, left: 0, zIndex: 0 }}
      >
        {/* Platform background glow */}
        <circle
          cx="50%"
          cy="50%"
          r="80"
          fill="rgba(234, 179, 8, 0.1)"
        />

        {/* Connection lines with arrows */}
        <defs>
          <marker
            id="arrowhead"
            markerWidth="10"
            markerHeight="7"
            refX="0"
            refY="3.5"
            orient="auto"
          >
            <polygon points="0 0, 10 3.5, 0 7" fill="#eab308" />
          </marker>
        </defs>

        <line
          x1="25%"
          y1="35%"
          x2="47%"
          y2="48%"
          style={{
            stroke: '#eab308',
            strokeWidth: 4,
            strokeDasharray: '8,4',
            markerEnd: 'url(#arrowhead)'
          }}
        />
        <line
          x1="75%"
          y1="35%"
          x2="53%"
          y2="48%"
          style={{
            stroke: '#eab308',
            strokeWidth: 4,
            strokeDasharray: '8,4',
            markerEnd: 'url(#arrowhead)'
          }}
        />
        <line
          x1="50%"
          y1="52%"
          x2="50%"
          y2="72%"
          style={{
            stroke: '#eab308',
            strokeWidth: 4,
            strokeDasharray: '8,4',
            markerEnd: 'url(#arrowhead)'
          }}
        />
      </svg>

      {/* Animated elements */}
      <Box
        ref={mineralRef}
        sx={{
          position: 'absolute',
          top: '30%',
          left: '15%',
          width: 50,
          height: 50,
          borderRadius: '50%',
          bgcolor: '#4caf50',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 4px 12px rgba(76, 175, 80, 0.5)',
          zIndex: 3,
          visibility: 'hidden'
        }}
      >
        <Typography variant="body1" sx={{ color: 'white', fontWeight: 'bold', fontSize: '1.5rem' }}>
          💎
        </Typography>
      </Box>

      <Box
        ref={documentRef}
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          width: 50,
          height: 50,
          borderRadius: '50%',
          bgcolor: '#2196f3',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 4px 12px rgba(33, 150, 243, 0.5)',
          zIndex: 3,
          visibility: 'hidden'
        }}
      >
        <Typography variant="body1" sx={{ color: 'white', fontWeight: 'bold', fontSize: '1.5rem' }}>
          📄
        </Typography>
      </Box>

      <Box
        ref={paymentRef}
        sx={{
          position: 'absolute',
          top: '30%',
          right: '15%',
          width: 50,
          height: 50,
          borderRadius: '50%',
          bgcolor: '#ff9800',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 4px 12px rgba(255, 152, 0, 0.5)',
          zIndex: 3,
          visibility: 'hidden'
        }}
      >
        <Typography variant="body1" sx={{ color: 'white', fontWeight: 'bold', fontSize: '1.5rem' }}>
          💰
        </Typography>
      </Box>

      {/* Legend */}
      <Box
        sx={{
          position: 'absolute',
          bottom: 15,
          left: '50%',
          transform: 'translateX(-50%)',
          display: 'flex',
          gap: 3,
          bgcolor: 'rgba(255,255,255,0.9)',
          borderRadius: 2,
          p: 1.5,
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          border: '1px solid rgba(234, 179, 8, 0.3)'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ width: 16, height: 16, borderRadius: '50%', bgcolor: '#4caf50', boxShadow: '0 2px 4px rgba(76, 175, 80, 0.3)' }} />
          <Typography variant="body2" sx={{ fontWeight: 'medium' }}>Minerals</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ width: 16, height: 16, borderRadius: '50%', bgcolor: '#2196f3', boxShadow: '0 2px 4px rgba(33, 150, 243, 0.3)' }} />
          <Typography variant="body2" sx={{ fontWeight: 'medium' }}>Documents</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ width: 16, height: 16, borderRadius: '50%', bgcolor: '#ff9800', boxShadow: '0 2px 4px rgba(255, 152, 0, 0.3)' }} />
          <Typography variant="body2" sx={{ fontWeight: 'medium' }}>Payments</Typography>
        </Box>
      </Box>
    </Paper>
  );
};

export default TradingPlatformAnimation;
